/**
 * T FOR ALL — local wishlist (no app, no dependencies).
 * Stores saved products in localStorage and powers:
 *   - heart toggle buttons:      [data-wishlist-toggle] with data-id/handle/title/url/image/price
 *   - header open button + count: [data-wishlist-open], [data-wishlist-count]
 *   - the wishlist drawer:        [data-wishlist-drawer] containing [data-wishlist-list]
 * Emits `tfa:wishlist:change` on document whenever the list changes.
 */
(function () {
  'use strict';
  var KEY = 'tfa:wishlist:v1';

  function read() {
    try {
      var raw = localStorage.getItem(KEY);
      var arr = raw ? JSON.parse(raw) : [];
      return Array.isArray(arr) ? arr : [];
    } catch (e) {
      return [];
    }
  }
  function write(list) {
    try {
      localStorage.setItem(KEY, JSON.stringify(list));
    } catch (e) {
      /* storage full / disabled — fail silently */
    }
    document.dispatchEvent(new CustomEvent('tfa:wishlist:change', { detail: { count: list.length } }));
  }

  function has(id) {
    id = String(id);
    return read().some(function (it) { return String(it.id) === id; });
  }
  function remove(id) {
    id = String(id);
    write(read().filter(function (it) { return String(it.id) !== id; }));
  }
  function add(item) {
    var list = read();
    if (list.some(function (it) { return String(it.id) === String(item.id); })) return;
    list.unshift(item);
    write(list);
  }
  function toggle(item) {
    if (has(item.id)) { remove(item.id); return false; }
    add(item); return true;
  }

  function itemFromButton(btn) {
    return {
      id: btn.getAttribute('data-id'),
      handle: btn.getAttribute('data-handle') || '',
      title: btn.getAttribute('data-title') || '',
      url: btn.getAttribute('data-url') || '',
      image: btn.getAttribute('data-image') || '',
      price: btn.getAttribute('data-price') || ''
    };
  }

  /* ---- reflect saved state on every heart button ---- */
  function syncButtons() {
    var saved = {};
    read().forEach(function (it) { saved[String(it.id)] = true; });
    document.querySelectorAll('[data-wishlist-toggle]').forEach(function (btn) {
      var on = !!saved[String(btn.getAttribute('data-id'))];
      btn.classList.toggle('is-saved', on);
      btn.setAttribute('aria-pressed', on ? 'true' : 'false');
    });
  }

  /* ---- header count bubble ---- */
  function syncCount() {
    var n = read().length;
    document.querySelectorAll('[data-wishlist-count]').forEach(function (el) {
      el.textContent = n;
      el.hidden = n === 0;
    });
  }

  /* ---- drawer ---- */
  function drawer() { return document.querySelector('[data-wishlist-drawer]'); }

  function escapeHtml(s) {
    return String(s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  function renderDrawer() {
    var d = drawer();
    if (!d) return;
    var list = read();
    var wrap = d.querySelector('[data-wishlist-list]');
    var empty = d.querySelector('[data-wishlist-empty]');
    if (!wrap) return;
    if (!list.length) {
      wrap.innerHTML = '';
      if (empty) empty.hidden = false;
      return;
    }
    if (empty) empty.hidden = true;
    wrap.innerHTML = list.map(function (it) {
      var img = it.image
        ? '<img src="' + escapeHtml(it.image) + '" alt="" loading="lazy" width="80" height="107">'
        : '<span class="tfa-wl-card__noimg"></span>';
      return (
        '<li class="tfa-wl-card">' +
          '<a class="tfa-wl-card__media" href="' + escapeHtml(it.url) + '">' + img + '</a>' +
          '<div class="tfa-wl-card__info">' +
            '<a class="tfa-wl-card__title" href="' + escapeHtml(it.url) + '">' + escapeHtml(it.title) + '</a>' +
            (it.price ? '<span class="tfa-wl-card__price">' + escapeHtml(it.price) + '</span>' : '') +
          '</div>' +
          '<button type="button" class="tfa-wl-card__remove" data-wishlist-remove="' + escapeHtml(it.id) + '" aria-label="Remove">&times;</button>' +
        '</li>'
      );
    }).join('');
  }

  function openDrawer() {
    var d = drawer();
    if (!d) return;
    renderDrawer();
    d.classList.add('is-open');
    d.setAttribute('aria-hidden', 'false');
    document.body.classList.add('tfa-wl-open');
  }
  function closeDrawer() {
    var d = drawer();
    if (!d) return;
    d.classList.remove('is-open');
    d.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('tfa-wl-open');
  }

  /* ---- events (delegated, survives section re-renders) ---- */
  document.addEventListener('click', function (e) {
    var toggleBtn = e.target.closest('[data-wishlist-toggle]');
    if (toggleBtn) {
      e.preventDefault();
      var nowSaved = toggle(itemFromButton(toggleBtn));
      toggleBtn.classList.toggle('is-saved', nowSaved);
      toggleBtn.setAttribute('aria-pressed', nowSaved ? 'true' : 'false');
      return;
    }
    if (e.target.closest('[data-wishlist-open]')) { e.preventDefault(); openDrawer(); return; }
    if (e.target.closest('[data-wishlist-close]')) { e.preventDefault(); closeDrawer(); return; }
    var rm = e.target.closest('[data-wishlist-remove]');
    if (rm) { e.preventDefault(); remove(rm.getAttribute('data-wishlist-remove')); renderDrawer(); return; }
  });
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape') closeDrawer();
  });

  document.addEventListener('tfa:wishlist:change', function () {
    syncButtons();
    syncCount();
    renderDrawer();
  });

  function init() { syncButtons(); syncCount(); }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  // Re-sync hearts after Dawn re-renders product/section HTML.
  document.addEventListener('shopify:section:load', syncButtons);

  window.TFAWishlist = { read: read, has: has, add: add, remove: remove, toggle: toggle, open: openDrawer, close: closeDrawer };
})();
